
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslations } from "@/hooks/useTranslations";
import { useAuth } from "@/components/AuthProvider";

export const useTrainingSessions = (showStudentSessions: boolean) => {
  const queryClient = useQueryClient();
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const { t } = useTranslations("training-sessions");
  const { user } = useAuth();

  const { data: sessions, isLoading } = useQuery({
    queryKey: ["training-sessions", showStudentSessions, user?.id],
    queryFn: async () => {
      console.log("Fetching training sessions...");
      if (!user?.id) {
        console.error("No user found");
        return [];
      }

      // Fetch custom exercises first for later matching
      const { data: customExercises, error: customExerciseError } = await supabase
        .from("custom_exercises")
        .select("*")
        .eq("trainer_id", user.id);

      if (customExerciseError) {
        console.error("Error fetching custom exercises:", customExerciseError);
      }

      console.log("Custom exercises found:", customExercises?.length || 0);

      // Create a map for efficient lookup (using lowercase name for case-insensitive matching)
      const customExerciseMap = new Map(
        customExercises?.map(ce => [ce.name.toLowerCase(), ce]) || []
      );

      const { data: trainerProfile } = await supabase
        .from("profiles")
        .select("is_trainer")
        .eq("id", user.id)
        .maybeSingle();

      if (!trainerProfile?.is_trainer) {
        const { data, error } = await supabase
          .from("training_sessions")
          .select(`
            *,
            student:students!training_sessions_student_id_fkey(
              id,
              first_name,
              last_name
            ),
            workout:workouts(
              title,
              exercises(*)
            ),
            trainer:profiles!training_sessions_trainer_id_fkey(
              id,
              first_name,
              last_name
            )
          `)
          .or(`trainer_id.eq.${user.id},student_id.eq.${user.id}`)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching training sessions:", error);
          throw error;
        }

        const processedSessions = data?.map(session => {
          if (session.workout?.exercises) {
            session.workout.exercises = session.workout.exercises.map(exercise => {
              const exerciseName = exercise.name?.toLowerCase() || '';
              const customExercise = customExerciseMap.get(exerciseName);
              
              if (customExercise) {
                console.log(`Exercise "${exercise.name}" matched as custom exercise:`, customExercise);
                return {
                  ...exercise,
                  isCustom: true,
                  image_url: customExercise.image_url,
                  body_part: customExercise.body_part || exercise.body_part,
                  target_muscle: customExercise.target_muscle || exercise.target_muscle
                };
              }
              
              return {
                ...exercise,
                isCustom: false
              };
            });
          }
          return session;
        });

        return processedSessions || [];
      }

      const { data: students, error: studentsError } = await supabase
        .from("students")
        .select("id")
        .eq("trainer_id", user.id);

      if (studentsError) {
        console.error("Error fetching students:", studentsError);
        throw studentsError;
      }

      if (!students || students.length === 0) {
        console.log("No students found for this trainer");
        return [];
      }

      const studentIds = students.map(student => student.id);
      console.log("Student IDs:", studentIds);

      let query = supabase
        .from("training_sessions")
        .select(`
          *,
          student:students!training_sessions_student_id_fkey(
            id,
            first_name,
            last_name
          ),
          workout:workouts(
            title,
            exercises(*)
          ),
          trainer:profiles!training_sessions_trainer_id_fkey(
            id,
            first_name,
            last_name
          )
        `)
        .in("student_id", studentIds);
      
      if (!showStudentSessions) {
        query = query.eq("created_by_type", "trainer");
      }
      
      const { data: trainingSessions, error: sessionsError } = await query
        .order("created_at", { ascending: false });

      if (sessionsError) {
        console.error("Error fetching training sessions:", sessionsError);
        throw sessionsError;
      }

      console.log("Training sessions fetched:", trainingSessions?.length || 0);
      
      // Process sessions to correctly mark custom exercises
      const processedSessions = trainingSessions?.map(session => {
        if (session.workout?.exercises) {
          session.workout.exercises = session.workout.exercises.map(exercise => {
            if (exercise && typeof exercise === 'object' && 'name' in exercise) {
              const exerciseName = exercise.name?.toLowerCase() || '';
              const customExercise = customExerciseMap.get(exerciseName);
              
              if (customExercise) {
                console.log(`Exercise "${exercise.name}" matched as custom exercise:`, customExercise);
                return {
                  ...exercise,
                  isCustom: true,
                  image_url: customExercise.image_url,
                  body_part: customExercise.body_part || exercise.body_part,
                  target_muscle: customExercise.target_muscle || exercise.target_muscle,
                  instructions: customExercise.instructions || exercise.instructions
                };
              }
              
              return {
                ...exercise,
                isCustom: false
              };
            }
            console.warn("Encountered invalid exercise object:", exercise);
            return exercise;
          });
        }
        return session;
      });
      
      const studentCreatedSessions = processedSessions?.filter(
        session => session.created_by_type === 'student'
      );
      console.log("Sessions created by students:", studentCreatedSessions?.length || 0);

      return processedSessions || [];
    },
    enabled: !!user?.id
  });

  const handleDeleteClick = (sessionId: string) => {
    setSessionToDelete(sessionId);
  };

  const handleDeleteCancel = () => {
    setSessionToDelete(null);
  };

  const handleDelete = async () => {
    if (!sessionToDelete || !user?.id) return;
    
    try {
      setDeletingSession(true);
      
      const { data: sessionData, error: verifyError } = await supabase
        .from("training_sessions")
        .select(`
          student_id,
          student:students!training_sessions_student_id_fkey(
            trainer_id
          )
        `)
        .eq("id", sessionToDelete)
        .single();
        
      if (verifyError || !sessionData || sessionData.student?.trainer_id !== user.id) {
        throw new Error("Session not found or you don't have permission to delete");
      }
      
      const { error } = await supabase
        .from("training_sessions")
        .delete()
        .eq("id", sessionToDelete);

      if (error) throw error;

      toast.success(t("notifications.deleted"));
      queryClient.invalidateQueries({ queryKey: ["training-sessions"] });
    } catch (error: any) {
      console.error("Error deleting training session:", error);
      toast.error(error.message || t("notifications.error"));
    } finally {
      setSessionToDelete(null);
      setDeletingSession(false);
    }
  };

  return {
    sessions,
    isLoading,
    sessionToDelete,
    deletingSession,
    handleDeleteClick,
    handleDeleteCancel,
    handleDelete
  };
};
